/**
 * KanbanContextAdapterOrganism
 *
 * Bridges context execution data to a Kanban board layout.
 * Groups spheres into lanes based on a configured property (e.g. flow stage, status).
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { useContextData } from '../../../hooks/useContextData';
import { useContextView } from '../../../hooks/useContextView';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { SphereData } from '../../../hooks/useContextData';

export interface KanbanContextAdapterOrganismProps {
  /** API clients from context */
  apiClients: ApiClients | null;
  /** Context view system name to load */
  viewSystemName: string;
  /** Root sphere ID to execute context from */
  rootSphereId: string;
  /** Results per page per level */
  limit?: number;
  /** Max depth for repeat levels */
  depth?: number;
  /** Callback when a card is selected */
  onCardSelect?: (sphereId: string) => void;
  /** Callback when a card is moved between lanes */
  onCardMove?: (sphereId: string, fromLane: string, toLane: string) => void;
  /** Custom render for loading state */
  renderLoading?: () => React.ReactNode;
  /** Custom render for error state */
  renderError?: (error: string) => React.ReactNode;
  /** CSS class name */
  className?: string;
}

/** Groups spheres by property value into lanes */
function groupByProperty(
  spheres: SphereData[],
  property: string,
  laneOrder?: string[],
  fallbackLabel?: string
): Record<string, SphereData[]> {
  const lanes: Record<string, SphereData[]> = {};

  if (laneOrder) {
    for (const lane of laneOrder) {
      lanes[lane] = [];
    }
  }

  for (const sphere of spheres) {
    const raw = sphere[property];
    const value = String(raw != null && typeof raw !== 'object' ? raw : (fallbackLabel ?? ''));
    if (!lanes[value]) lanes[value] = [];
    lanes[value].push(sphere);
  }

  return lanes;
}

const KanbanContextAdapterOrganism: React.FC<KanbanContextAdapterOrganismProps> = ({
  apiClients,
  viewSystemName,
  rootSphereId,
  limit,
  depth,
  onCardSelect,
  onCardMove,
  renderLoading,
  renderError,
  className,
}) => {
  const { t } = useTranslation();
  const { view, loading: viewLoading, error: viewError } = useContextView(apiClients, viewSystemName);

  const contextSystemName = view?.context_ref ?? '';

  const {
    data,
    loading: dataLoading,
    error: dataError,
    cacheMetadata,
    refresh,
    fetchMore,
    hasMore,
  } = useContextData(apiClients, {
    systemName: contextSystemName,
    rootSphereId,
    limit,
    depth,
  });

  const loading = viewLoading || dataLoading;
  const error = viewError || dataError;

  const kanbanConfig = useMemo(() => {
    if (!view) return null;
    const vc = view.view_config;
    return {
      laneProperty: (vc.laneProperty as string) ?? 'status',
      laneOrder: vc.laneOrder as string[] | undefined,
      swimlaneProperty: vc.swimlaneProperty as string | undefined,
      cardFields: vc.cardFields as string[] | undefined,
      cardSize: (vc.cardSize as string) ?? 'standard',
      allowDragBetweenLanes: vc.allowDragBetweenLanes as boolean | undefined,
      wipLimits: vc.wipLimits as Record<string, number> | undefined,
    };
  }, [view]);

  const lanes = useMemo(() => {
    if (!data || !kanbanConfig) return {};
    const allSpheres = Object.values(data.spheresByColumn).flat();
    return groupByProperty(allSpheres, kanbanConfig.laneProperty, kanbanConfig.laneOrder, t('context.common.unassigned'));
  }, [data, kanbanConfig, t]);

  if (loading && !data) {
    if (renderLoading) return <>{renderLoading()}</>;
    return <div className={className}>{t('context.common.loadingKanban')}</div>;
  }

  if (error && !data) {
    if (renderError) return <>{renderError(error)}</>;
    return <div className={className}>{t('context.common.error', { message: error })}</div>;
  }

  if (!data || !view || !kanbanConfig) {
    return null;
  }

  return (
    <div className={className} data-testid="kanban-context-adapter">
      <div className="kanban-context-adapter__header">
        <span className="kanban-context-adapter__title">
          {view.name?.default ?? view.system_name}
        </span>
        <button
          className="kanban-context-adapter__refresh"
          onClick={refresh}
          title={t('context.common.refreshData')}
          type="button"
        >
          ↻
        </button>
      </div>
      <div className="kanban-context-adapter__board">
        {Object.entries(lanes).map(([laneName, spheres]) => {
          const wipLimit = kanbanConfig.wipLimits?.[laneName];
          const overLimit = wipLimit !== undefined && spheres.length > wipLimit;
          return (
            <div
              key={laneName}
              className={`kanban-context-adapter__lane ${overLimit ? 'kanban-context-adapter__lane--over-limit' : ''}`}
            >
              <div className="kanban-context-adapter__lane-header">
                <span className="kanban-context-adapter__lane-name">{laneName}</span>
                <span className="kanban-context-adapter__lane-count">
                  {spheres.length}
                  {wipLimit === undefined ? '' : ` / ${wipLimit}`}
                </span>
              </div>
              <div className="kanban-context-adapter__lane-cards">
                {spheres.map((sphere) => (
                  <button
                    type="button"
                    key={sphere.id}
                    className={`kanban-context-adapter__card kanban-context-adapter__card--${kanbanConfig.cardSize}`}
                    onClick={() => onCardSelect?.(sphere.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onCardSelect?.(sphere.id);
                    }}
                    tabIndex={0}
                  >
                    <span className="kanban-context-adapter__card-label">
                      {String(sphere.label ?? sphere.id)}
                    </span>
                    {kanbanConfig.cardFields?.map((field) =>
                      sphere[field] == null ? null : (
                        <span key={field} className="kanban-context-adapter__card-field">
                          {String(sphere[field])}
                        </span>
                      )
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <button
          className="kanban-context-adapter__load-more"
          onClick={fetchMore}
          type="button"
        >
          {t('context.common.loadMore')}
        </button>
      )}
      {cacheMetadata && (
        <div className="kanban-context-adapter__cache-info" data-testid="cache-info">
          {cacheMetadata.structureHit ? t('context.common.cached') : t('context.common.fresh')} · {data.stats.totalLoaded} {t('context.common.spheres')}
        </div>
      )}
    </div>
  );
};

export default KanbanContextAdapterOrganism;
